import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Wallet, GraduationCap, Globe2, Award, MapPin, Users, Calendar, BookOpen, CheckCircle2, ExternalLink, Trophy, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Webinars from "../components/Webinars";
import ApplicationDialog from "../components/ApplicationDialog";
import { universityData, defaultUniversityInfo } from "../data/universityData";
import { useLang } from "../i18n/LangContext";

const LABELS = {
  ru: { back: "К списку вузов", profile: "Профиль учебного заведения", tuition: "Стоимость в год", level: "Уровень", language: "Язык", country: "Страна", founded: "Основан", students: "Студенты", ranking: "Рейтинг", deadline: "Дедлайн", about: "О вузе", programs: "Программы и направления", requirements: "Требования к поступлению", why: "Почему стоит выбрать", partner: "STUDYGRI — официальный партнёр", consult: "Записаться на консультацию", site: "Сайт вуза", noData: "Подробная информация будет добавлена командой STUDYGRI" },
  uk: { back: "До списку вишів", profile: "Профіль закладу", tuition: "Вартість на рік", level: "Рівень", language: "Мова", country: "Країна", founded: "Засновано", students: "Студенти", ranking: "Рейтинг", deadline: "Дедлайн", about: "Про виш", programs: "Програми та напрями", requirements: "Вимоги до вступу", why: "Чому варто обрати", partner: "STUDYGRI — офіційний партнер", consult: "Записатися на консультацію", site: "Сайт вишу", noData: "Детальна інформація буде додана командою STUDYGRI" },
  en: { back: "Back to universities", profile: "University Profile", tuition: "Annual tuition", level: "Level", language: "Language", country: "Country", founded: "Founded", students: "Students", ranking: "Ranking", deadline: "Deadline", about: "About", programs: "Programmes & Specialisations", requirements: "Entry requirements", why: "Why choose this university", partner: "STUDYGRI — official partner", consult: "Book a consultation", site: "Visit website", noData: "Detailed info will be added by STUDYGRI team" }
};

const UniversityPage = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const labels = LABELS[lang] || LABELS.ru;
  const [formOpen, setFormOpen] = useState(false);

  const uni = universityData[slug];
  const fallbackName = decodeURIComponent(slug || "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const info = uni ? { ...defaultUniversityInfo, ...uni } : { ...defaultUniversityInfo, name: fallbackName };
  const isRtl = lang === 'ar';

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }} dir={isRtl ? 'rtl' : 'ltr'}>
      <Header onOpenForm={() => setFormOpen(true)} />

      <div className="pt-28 md:pt-36 pb-20" data-testid="university-page">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <Link to="/#universities" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 transition hover:gap-3" style={{ color: '#C9A96E' }} data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> {labels.back}
          </Link>

          {/* Hero */}
          <div className="flex items-center gap-3 mb-3">
            {info.flag && <span className="text-3xl">{info.flag}</span>}
            <div className="eyebrow">{labels.profile} · {info.type}</div>
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-tight" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.025em' }}>{info.name}</h1>
          {info.tagline && <p className="mt-5 text-lg md:text-xl max-w-3xl leading-relaxed" style={{ color: '#555' }}>{info.tagline}</p>}

          {/* Stats row */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {info.country && (
              <Stat icon={MapPin} label={labels.country} value={`${info.country}${info.city ? ' · ' + info.city : ''}`} />
            )}
            {info.founded && <Stat icon={Calendar} label={labels.founded} value={info.founded} />}
            {info.students && <Stat icon={Users} label={labels.students} value={info.students} />}
            {info.ranking && <Stat icon={Trophy} label={labels.ranking} value={info.ranking} />}
          </div>

          {/* Key info card */}
          <div className="mt-8 rounded-3xl p-8 md:p-10" style={{ background: '#fff', border: '1px solid #E8E0D5' }}>
            <div className="grid md:grid-cols-3 gap-6 mb-8 pb-8" style={{ borderBottom: '1px solid #E8E0D5' }}>
              <KeyValue Icon={Wallet} label={labels.tuition} value={info.feeTo ? `${info.feeFrom} – ${info.feeTo}` : info.feeFrom} />
              <KeyValue Icon={GraduationCap} label={labels.level} value={info.type} />
              <KeyValue Icon={Globe2} label={labels.language} value={info.language} />
              {info.deadline && <KeyValue Icon={Calendar} label={labels.deadline} value={info.deadline} />}
              {uni && <KeyValue Icon={BookOpen} label={labels.programs} value={`${info.programs.length}+ programmes`} />}
              {info.website && (
                <a href={info.website} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group" data-testid="external-website">
                  <ExternalLink className="w-5 h-5 mt-1 transition group-hover:translate-x-0.5" style={{ color: '#C9A96E' }} />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{labels.site}</div>
                    <div className="font-display text-base mt-1 transition group-hover:opacity-70" style={{ color: '#1B2A4A' }}>{info.website.replace(/https?:\/\//, '')}</div>
                  </div>
                </a>
              )}
            </div>

            {/* Programs */}
            {uni && info.programs && (
              <div className="mb-8">
                <h2 className="font-display text-2xl mb-5" style={{ color: '#1B2A4A', fontWeight: 800 }}>{labels.programs}</h2>
                <div className="flex flex-wrap gap-2">
                  {info.programs.map((p, i) => (
                    <span key={i} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: '#FAF8F5', border: '1px solid #E8E0D5', color: '#1B2A4A' }} data-testid={`program-${i}`}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Requirements */}
            {uni && info.requirements && (
              <div className="mb-8">
                <h2 className="font-display text-2xl mb-5" style={{ color: '#1B2A4A', fontWeight: 800 }}>{labels.requirements}</h2>
                <ul className="space-y-2">
                  {info.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-base" style={{ color: '#3a4f4f' }}>
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A96E' }} strokeWidth={1.8} />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Why choose */}
            <div className="mb-8">
              <h2 className="font-display text-2xl mb-5" style={{ color: '#1B2A4A', fontWeight: 800 }}>{labels.why}</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {info.why.map((w, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ background: '#FAF8F5' }}>
                    <Award className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#C9A96E' }} strokeWidth={1.6} />
                    <span className="text-sm leading-relaxed" style={{ color: '#2C2C2C' }}>{w}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest font-bold pt-4" style={{ color: '#7A6A4E', borderTop: '1px solid #E8E0D5' }}>
              <Award className="w-3.5 h-3.5" style={{ color: '#C9A96E' }} /> {labels.partner}
            </div>

            <button onClick={() => setFormOpen(true)} className="mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-white transition hover:opacity-90" style={{ background: '#1B2A4A' }} data-testid="uni-consult-btn">
              {labels.consult} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {!uni && (
            <div className="mt-6 p-4 rounded-xl text-sm" style={{ background: '#FAF8F5', border: '1px dashed #C9A96E', color: '#7A6A4E' }}>
              ℹ️ {labels.noData}
            </div>
          )}
        </div>
      </div>

      <Webinars />
      <Footer />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl p-4" style={{ background: '#fff', border: '1px solid #E8E0D5' }}>
    <Icon className="w-4 h-4 mb-2" style={{ color: '#C9A96E' }} />
    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#7A6A4E' }}>{label}</div>
    <div className="font-display text-sm font-semibold leading-snug" style={{ color: '#1B2A4A' }}>{value}</div>
  </div>
);

const KeyValue = ({ Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 mt-1" style={{ color: '#C9A96E' }} />
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#7A6A4E' }}>{label}</div>
      <div className="font-display text-base mt-1" style={{ color: '#1B2A4A', fontWeight: 600 }}>{value}</div>
    </div>
  </div>
);

export default UniversityPage;
